
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface User {
  _id: string;
  name: string;
  emailId: string;
  status: string;
  supplierId?: string;
  designerId?: string;
  signedUpDate?: string; // Add signed up date field
  createdAt?: string; // Alternative date field name
  created_at?: string; // Alternative date field name
  registrationDate?: string; // Alternative date field name
}

export default function UserApprovalTabs() {
  const [designers, setDesigners] = useState<User[]>([]);
  const [suppliers, setSuppliers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState('supplier');
  const [loading, setLoading] = useState(false);
  const [approvingUsers, setApprovingUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://api.nestingale.com/v1/api/partner');
      
      if (!res.ok) {
        throw new Error(`Failed to fetch users: ${res.status} ${res.statusText}`);
      }
      
      const data = await res.json();
      console.log('API Response:', data); // Debug log to see the actual API response
      
      setDesigners(data?.designers || []);
      setSuppliers(data?.suppliers || []);
    } catch (error) {
      console.error('Failed to fetch users', error);
      toast.error(`Failed to load users: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (partnerType: string, partnerId: string, userId: string) => {
    // Prevent multiple clicks
    if (approvingUsers.has(userId)) {
      return;
    }

    setApprovingUsers(prev => new Set(prev).add(userId));

    try {
      console.log('Approving partner:', { partnerType, partnerId });
      
      const res = await fetch('https://api.nestingale.com/v1/api/approvePartner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ partnerType, partnerId }),
      });

      let responseData;
      try {
        responseData = await res.json();
        console.log('API Response:', responseData);
      } catch (parseError) {
        console.log('Response text:', await res.text());
        throw new Error('Invalid JSON response from server');
      }

      if (!res.ok) {
        throw new Error(`Status update failed: ${responseData?.message || res.statusText}`);
      }

      // Show success message
      toast.success(`Successfully approved ${partnerType.toLowerCase()}`);

      // Update the local state to reflect the approval
      if (activeTab === 'supplier') {
        setSuppliers((prevSuppliers) =>
          prevSuppliers.map((user) =>
            user.supplierId === partnerId ? { ...user, status: 'approved' } : user
          )
        );
      } else {
        setDesigners((prevDesigners) =>
          prevDesigners.map((user) =>
            user.designerId === partnerId ? { ...user, status: 'approved' } : user
          )
        );
      }

      // Optionally refresh the data to ensure consistency
      // await fetchUsers();
      
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(`Failed to update status: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setApprovingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Function to format the signed up date
  const formatSignedUpDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      // Handle different date formats that might come from the API
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const filteredUsers = activeTab === 'supplier' ? suppliers : designers;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="flex justify-center mb-4">
        <button
          onClick={() => setActiveTab('supplier')}
          className={`px-4 py-2 rounded-l-md text-sm font-medium border ${
            activeTab === 'supplier'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-black'
          }`}
        >
          Suppliers
        </button>
        <button
          onClick={() => setActiveTab('designer')}
          className={`px-4 py-2 rounded-r-md text-sm font-medium border-l-0 border ${
            activeTab === 'designer'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-black'
          }`}
        >
          Designers
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map((user) => {
            const isApproved = user.status?.toLowerCase() === 'approved';
            
            return (
              <div
                key={user._id}
                className={`flex items-center justify-between p-4 rounded-lg border shadow-sm ${
                  isApproved 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{user.name}</h3>
                  <p className="text-sm text-gray-600">{user.emailId}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Status:</span>
                      <span className={`text-sm font-medium capitalize ${
                        isApproved ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {user.status}
                      </span>
                      {isApproved && (
                        <span className="text-green-600 text-sm">✓</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Signed up:</span>
                      <span className="text-sm text-gray-700">
                        {formatSignedUpDate(user.signedUpDate || user.createdAt || user.created_at || user.registrationDate)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {!isApproved && (
                  <button
                    onClick={() => {
                      const partnerType = activeTab === 'supplier' ? 'SUPPLIER' : 'DESIGNER';
                      const partnerId = activeTab === 'supplier' ? user?.supplierId : user?.designerId;
                      if (partnerId) {
                        updateStatus(partnerType, partnerId, user._id);
                      }
                    }}
                    disabled={approvingUsers.has(user._id)}
                    className={`text-sm px-4 py-2 rounded-md transition-colors ${
                      approvingUsers.has(user._id)
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    {approvingUsers.has(user._id) ? 'Approving...' : 'Approve'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
